// PHI: renders raw census cell values as the broker wrote them.

import { Stack } from "@mui/material";
import { Typography } from "@platform/design-system";

import { describeRows } from "../rows";

import type {
  UnreadableColumn,
  UnreadableValueGroup,
} from "../hooks/useCensusValidation";

interface CensusUnreadableValuesProps {
  readonly counts: Record<string, number>;
  readonly columns: readonly UnreadableColumn[];
}

const describeGroup = ({ value, rows }: UnreadableValueGroup): string => {
  if (value === undefined) {
    return `${describeRows(rows)} (value not shown)`;
  }

  const repeats = rows.length > 1 ? ` ×${rows.length}` : "";

  return `"${value}"${repeats} (${describeRows(rows)})`;
};

interface ColumnLine {
  readonly column: string;
  readonly count: number;
  readonly detail: string;
}

const detailedLine = ({ column, values }: UnreadableColumn): ColumnLine => ({
  column,
  count: values.reduce((total, { rows }) => total + rows.length, 0),
  detail: values.map(describeGroup).join(" · "),
});

/**
 * Columns with rows recorded come first, in the order they appear in the
 * sheet. A census read before rows were recorded has counts only, and those
 * columns follow with their count alone.
 */
const columnLines = (
  counts: Record<string, number>,
  columns: readonly UnreadableColumn[],
): ColumnLine[] => {
  const detailedColumns = new Set(columns.map(({ column }) => column));
  const countOnly = Object.entries(counts)
    .filter(([column]) => !detailedColumns.has(column))
    .map(([column, count]) => ({ column, count, detail: "" }));

  return [...columns.map(detailedLine), ...countOnly];
};

/** Each column with values we could not read, how many, and where they sit. */
export const CensusUnreadableValues = ({
  counts,
  columns,
}: CensusUnreadableValuesProps): React.JSX.Element => (
  <Stack gap={0.5} data-testid="census-unreadable-values">
    {columnLines(counts, columns).map(({ column, count, detail }) => (
      <Typography
        key={column}
        variant="body-medium"
        data-testid="census-unreadable-column"
      >
        {column} ({count}){detail && `: ${detail}`}
      </Typography>
    ))}
  </Stack>
);
